import { useState } from "react";
import { toast } from "sonner";
import api, { apiError, brl, parseMoney } from "@/lib/api";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Input, Select, Textarea } from "@/components/ui-kit";
import SupplierPicker from "@/components/panel/pickers/SupplierPicker";
import CustomerPicker from "@/components/panel/pickers/CustomerPicker";

const hoje = () => new Date().toISOString().slice(0, 10);

const VAZIO = {
  kind: "pagar", description: "", category: "", amount: "", due_date: hoje(),
  supplier_id: "", customer_id: "", notes: "",
};

/**
 * Lançamento e edição de uma conta.
 *
 * O sentido (pagar/receber) só aparece na CRIAÇÃO: trocá-lo depois inverteria o
 * sinal do lançamento e faria o saldo previsto do passado mentir — o servidor
 * recusa, e a tela não oferece o que não pode ser feito.
 *
 * O vínculo muda com o sentido: conta a pagar aponta um fornecedor, conta a
 * receber aponta um cliente. Mostrar os dois campos sempre convidaria a
 * preencher o errado.
 */
export function AccountFormDialog({ entry, onClose, onSaved }) {
  const editando = Boolean(entry);
  const [form, setForm] = useState(entry ? { ...VAZIO, ...entry } : { ...VAZIO });
  const [supplierName, setSupplierName] = useState(entry?.supplier_name || "");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pagar = form.kind === "pagar";

  const save = async () => {
    if (!form.description.trim()) { toast.error("Descreva a conta"); return; }
    if (!(parseMoney(form.amount) > 0)) { toast.error("Informe o valor"); return; }
    if (!form.due_date) { toast.error("Informe o vencimento"); return; }

    const payload = {
      description: form.description.trim(),
      category: form.category?.trim() || null,
      amount: parseMoney(form.amount),
      due_date: form.due_date,
      supplier_id: pagar ? form.supplier_id || null : null,
      customer_id: pagar ? null : form.customer_id || null,
      notes: form.notes?.trim() || null,
    };

    setSaving(true);
    try {
      if (editando) await api.patch(`/admin/finance/entries/${entry.id}`, payload);
      else await api.post("/admin/finance/entries", { ...payload, kind: form.kind });
      toast.success(editando ? "Conta atualizada" : "Conta lançada");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} maxWidth="max-w-lg" testId="finance-entry-dialog"
      title={editando ? `Editar ${entry.description}` : "Lançar conta"}>
      <div className="space-y-3">
        {!editando && (
          <div role="group" aria-label="Tipo da conta" className="grid grid-cols-2 gap-2">
            {[["pagar", "A pagar", "text-red-600 border-red-300 bg-red-50"],
              ["receber", "A receber", "text-emerald-700 border-emerald-300 bg-emerald-50"]].map(([id, rotulo, ativo]) => (
              <button key={id} type="button" onClick={() => setForm({ ...form, kind: id })}
                aria-pressed={form.kind === id} data-testid={`finance-kind-${id}`}
                className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${
                  form.kind === id ? ativo : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                {rotulo}
              </button>
            ))}
          </div>
        )}

        <Field label="Descrição" required>
          <Input value={form.description} onChange={set("description")} maxLength={180}
            placeholder={pagar ? "Ex.: Aluguel da loja" : "Ex.: Cadeira levada por Dona Maria"}
            data-testid="finance-entry-description" autoFocus />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor (R$)" required>
            <Input inputMode="decimal" value={form.amount} onChange={set("amount")}
              placeholder="0,00" data-testid="finance-entry-amount" mono />
          </Field>
          <Field label="Vencimento" required>
            <Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="finance-entry-due" />
          </Field>
        </div>

        <Field label="Categoria" hint="Opcional — agrupa as contas nos filtros.">
          <Input value={form.category || ""} onChange={set("category")} maxLength={60}
            placeholder={pagar ? "Ex.: Ocupação, Fornecedor, Impostos" : "Ex.: Venda a prazo"}
            data-testid="finance-entry-category" />
        </Field>

        {pagar ? (
          <Field label="Fornecedor" hint="Opcional.">
            <SupplierPicker value={form.supplier_id}
              onChange={(id, label) => { setForm({ ...form, supplier_id: id }); setSupplierName(label); }}
              selectedLabel={supplierName} testId="finance-entry-supplier" />
          </Field>
        ) : (
          <Field label="Cliente" hint="Opcional.">
            <CustomerPicker value={form.customer_id}
              onChange={(c) => setForm({ ...form, customer_id: c?.id || "" })}
              placeholder="Buscar cliente" testId="finance-entry-customer" />
          </Field>
        )}

        <Field label="Observações">
          <Textarea value={form.notes || ""} onChange={set("notes")} rows={2} maxLength={500} />
        </Field>

        <Button onClick={save} loading={saving} size="lg" block data-testid="finance-entry-save">
          {editando ? "Salvar alterações" : pagar ? "Lançar conta a pagar" : "Lançar conta a receber"}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Baixa da conta.
 *
 * O valor pago vem preenchido com o previsto (é o caso comum) mas continua
 * editável, porque previsto e pago divergem de verdade: juro por atraso,
 * desconto por antecipação, arredondamento na negociação. É a diferença entre os
 * dois que explica o caixa no fim do mês.
 */
export function SettleDialog({ entry, onClose, onDone }) {
  const [amount, setAmount] = useState(String(entry.amount));
  const [date, setDate] = useState(hoje());
  const [saving, setSaving] = useState(false);
  const pagar = entry.kind === "pagar";

  const diferenca = Math.round((parseMoney(amount) - entry.amount) * 100) / 100;

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/finance/entries/${entry.id}/settle`, {
        paid_at: date, paid_amount: parseMoney(amount),
      });
      toast.success(pagar ? "Conta baixada como paga" : "Conta baixada como recebida");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={pagar ? "Registrar pagamento" : "Registrar recebimento"}
      testId="finance-settle-dialog">
      <p className="text-sm text-slate-600">
        <b>{entry.description}</b> — previsto <span className="font-mono">{brl(entry.amount)}</span> para{" "}
        {new Date(`${entry.due_date}T12:00:00`).toLocaleDateString("pt-BR")}.
      </p>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Field label={pagar ? "Valor pago (R$)" : "Valor recebido (R$)"} required>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid="finance-settle-amount" mono autoFocus />
        </Field>
        <Field label="Data" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="finance-settle-date" />
        </Field>
      </div>
      {Math.abs(diferenca) >= 0.01 && (
        <p className={`text-xs mt-2 ${diferenca > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="finance-settle-diff">
          {diferenca > 0 ? "Acima" : "Abaixo"} do previsto em <b className="font-mono">{brl(Math.abs(diferenca))}</b>
          {diferenca > 0 ? " (juro, multa?)" : " (desconto?)"}
        </p>
      )}
      <div className="flex gap-3 mt-5">
        <Button variant="secondary" block onClick={onClose}>Voltar</Button>
        <Button block loading={saving} onClick={confirm} data-testid="finance-settle-confirm">
          {pagar ? "Registrar pagamento" : "Registrar recebimento"}
        </Button>
      </div>
    </Modal>
  );
}

/** Select de período usado no filtro de contas. */
export function DueFilter({ value, onChange }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} data-testid="finance-entries-due-filter"
      aria-label="Filtrar por vencimento" className="w-auto px-2.5 text-slate-600">
      <option value="">Todos os vencimentos</option>
      <option value="7">Vence em 7 dias</option>
      <option value="30">Vence em 30 dias</option>
    </Select>
  );
}
